import { Injectable } from '@angular/core';

/**
 * Holds the JWT issued by Authenticate/token, in memory only.
 *
 * Nothing here is persisted. A page reload therefore starts with no token and the app asks
 * Authenticate/refresh for one (see TokenRefreshService and the APP_INITIALIZER in app.module.ts),
 * trading the HttpOnly refresh cookie the browser holds for a fresh access token.
 *
 * This replaced localStorage, and the reason is narrower than it looks: it does not stop an XSS.
 * A script running on the page can still call the refresh endpoint, or simply act through the app
 * while the page is open. What it stops is *exfiltration* - a field in localStorage can be read and
 * posted to an attacker's server, and the token then works from anywhere for its full two hours.
 * A variable in a closure cannot leave the page, so the attack shrinks from "steal a bearer token
 * and replay it later, elsewhere" to "act from inside this tab while it is open".
 *
 * Two consequences worth knowing, both deliberate:
 *
 * - Each tab holds its own token and refreshes independently. localStorage used to share one across
 *   tabs; nothing does now, and nothing should - a shared store is the thing being removed.
 * - Playwright's storageState() (e2e/global-setup.ts) captures cookies, so the refresh cookie is
 *   saved and replayed, and a replayed session bootstraps a token through the initializer. That is
 *   strictly better than before: the reason the previous comment here argued for localStorage over
 *   sessionStorage was that storageState could not see sessionStorage at all.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private accessToken: string | null = null;
  private expiry: Date | null = null;

  public setToken(accessToken: string, expiryUtc: string | Date): void {
    this.accessToken = accessToken;
    this.expiry = expiryUtc instanceof Date ? expiryUtc : new Date(expiryUtc);
  }

  /**
   * Stores a token the server minted to replace the one we hold, because it changed our claims
   * (see JwtInterceptor). The expiry comes from the token's own `exp` rather than a separate value:
   * a reissue deliberately carries the original expiry forward, so deriving it here keeps the two
   * from ever disagreeing. A token with no readable `exp` is not usable, so it is discarded rather
   * than stored with a guessed lifetime.
   *
   * Never moves the expiry backwards. Two things mint replacement tokens and both answer on the
   * same header: the heartbeat slides the window forward, while an ordinary claim change reissues
   * at the expiry of *the token that request carried*. A request already in flight when a heartbeat
   * lands therefore replies with a strictly older `exp`, and plain last-write-wins would undo the
   * slide - shortening the session by however long the request took, silently, on every org switch
   * that happened to overlap a heartbeat.
   *
   * Equal `exp` is still accepted, and that is the point of `<` rather than `<=`: a reissue inside
   * the same second as the stored token carries an identical expiry but *different claims*, and
   * dropping it would put the selected organization back out of sync with the data on screen -
   * exactly the failure the reissue mechanism exists to prevent.
   */
  public setTokenFromJwt(accessToken: string): void {
    const exp = this.readClaim(accessToken, 'exp');
    const secondsSinceEpoch = exp === null ? NaN : Number(exp);
    if (!Number.isFinite(secondsSinceEpoch) || secondsSinceEpoch <= 0) return;

    const incoming = new Date(secondsSinceEpoch * 1000);
    const current = this.getExpiry();
    if (current && !Number.isNaN(current.getTime()) && incoming.getTime() < current.getTime())
      return;

    this.setToken(accessToken, incoming);
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public getExpiry(): Date | null {
    return this.expiry;
  }

  /** True once there is no token, or it is within `bufferMs` of (or past) its own expiry. */
  public isExpired(bufferMs: number = 0): boolean {
    const token = this.getAccessToken();
    const expiry = this.getExpiry();
    if (!token || !expiry) return true;
    return expiry.getTime() - bufferMs <= Date.now();
  }

  public clearToken(): void {
    this.accessToken = null;
    this.expiry = null;
  }

  /** The signed-in user's culture (e.g. es-MX) from the token's claims; null with no token. */
  public getCultureCode(): string | null {
    return this.accessToken ? this.readClaim(this.accessToken, 'CultureCode') : null;
  }

  /** Reads a single claim out of a token's own payload; null if it is absent or unparseable. */
  private readClaim(token: string, claimType: string): string | null {
    try {
      const payload = token.split('.')[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const claims = JSON.parse(atob(padded));
      const value = claims[claimType];
      return value === undefined || value === null ? null : String(value);
    } catch {
      return null;
    }
  }
}
